"use client";

import StepHeading from "@/app/components/molecules/StepHeading";
import FormCard from "@/app/components/atoms/FormCard";
import InfoBanner from "@/app/components/atoms/InfoBanner";
import Button from "@/app/components/atoms/Button";
import DynamicForm from "@/app/components/organisms/DynamicForm";
import { trackAuthEvent } from "@/lib/analytics/gtag";
import type { FormConfig } from "@/app/components/types";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emailConfig: FormConfig = {
  id: "email-form",
  fields: [
    {
      name: "email",
      label: "Enter your email address",
      type: "email",
      placeholder: "Email Address",
      required: true,
      autoFocus: true,
    },
  ],
  submitLabel: "Continue",
  ariaLabelledBy: "login-header",
};

const validateEmail = (data: Record<string, string | boolean>) => {
  const errors: Record<string, string> = {};
  const email = String(data["email"] ?? "").trim();
  if (!EMAIL_REGEX.test(email)) {
    errors["email"] = "Please enter a valid email address";
  }
  return errors;
};

interface EmailStepProps {
  /** Callback fired when the user submits a valid email address. */
  onContinue: (email: string) => void;
  /** Whether the email check is in progress. */
  loading?: boolean;
  /** Error message to display below the form (e.g. "No account found"). */
  error?: string | null;
}

/**
 * @component EmailStep
 * @description First step of the login flow. Prompts the user to enter their
 * email address with a heading, labeled input, and continue button.
 * @param onContinue - Callback receiving the validated email when the user submits.
 * @param loading - Shows loading state on the submit button while checking email.
 * @param error - Form-level error message shown below the form.
 * @returns The email entry form.
 */
export default function EmailStep({ onContinue, loading, error }: EmailStepProps) {
  const handleSubmit = (data: Record<string, string | boolean>) => {
    trackAuthEvent("email_submitted", "success");
    onContinue(String(data["email"] ?? "").trim());
  };

  return (
    <div className="flex flex-col gap-8 ">
      <StepHeading id="email-step-heading" title="GFS Portal" />

      <div className="w-full max-w-md m-auto">
        <FormCard title="Login">
          <DynamicForm
            config={emailConfig}
            onSubmit={handleSubmit}
            validate={validateEmail}
            loading={loading}
          />
          {error && (
            <InfoBanner className="mt-2">
              {error}
            </InfoBanner>
          )}
          <p className="text-title text-charcoal mb-4 pt-4 border-t border-border-subtle">
            Create an account
          </p>
          <Button
            variant="outline"
            size="full"
            href="/register"
            onClick={() => {
              trackAuthEvent("register_cta_clicked");
            }}
          >
            Create Portal Account
          </Button>
        </FormCard>
      </div>
    </div>
  );
}
